import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Image, Plus, Trash2, Loader2, Monitor, Smartphone, Upload, Link, 
  Sparkles, Download, X, Wand2, ImagePlus, AlertTriangle, Pencil, Maximize2, Crop
} from "lucide-react";
import CreativeEditor from "@/components/CreativeEditor";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

// Creative format dimensions - FIXED, cannot be changed
const FORMATS = {
  BANNER: { 
    width: 640, 
    height: 200, 
    label: "Banner", 
    ratio: "640×200", 
    aspectRatio: 640 / 200, // 3.2:1
    icon: Smartphone,
    description: "Displayed in the Booma passenger app during active rides."
  },
  PANEL: { 
    width: 1920, 
    height: 1080, 
    label: "Panel", 
    ratio: "1920×1080 (16:9)", 
    aspectRatio: 16 / 9,
    icon: Monitor,
    description: "Shown on in-car drive panel during active Booma rides."
  }
};

export default function Creatives() {
  const { token } = useAuth();
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createMode, setCreateMode] = useState(null); // 'upload', 'ai', 'url'
  const [createFormat, setCreateFormat] = useState(null); // 'BANNER' or 'PANEL'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [creativeToDelete, setCreativeToDelete] = useState(null);
  
  // Editor state
  const [editingCreative, setEditingCreative] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  
  // Company assets state
  const [companyAssets, setCompanyAssets] = useState([]);
  const [companyBranding, setCompanyBranding] = useState(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadDimensions, setUploadDimensions] = useState(null);
  const [dimensionError, setDimensionError] = useState(null);
  
  // Crop states
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const cropImageRef = useRef(null);
  
  // URL crop states
  const [showUrlCrop, setShowUrlCrop] = useState(false);
  const [urlCrop, setUrlCrop] = useState(null);
  const [urlCompletedCrop, setUrlCompletedCrop] = useState(null);
  const [urlOriginalImage, setUrlOriginalImage] = useState(null);
  const urlCropImageRef = useRef(null);
  
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiName, setAiName] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urlName, setUrlName] = useState("");
  const [urlDimensions, setUrlDimensions] = useState(null);
  const [urlDimensionError, setUrlDimensionError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCreatives();
    fetchCompanyAssets();
    fetchCompanyBranding();
  }, []);

  const fetchCreatives = async () => {
    try {
      const response = await axios.get(`${API}/creatives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatives(response.data);
    } catch (error) {
      toast.error("Failed to fetch creatives");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyAssets = async () => {
    try {
      const response = await axios.get(`${API}/company-assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyAssets(response.data);
    } catch (error) {
      console.error("Failed to fetch company assets", error);
    }
  };

  const fetchCompanyBranding = async () => {
    try {
      const response = await axios.get(`${API}/company-branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyBranding(response.data);
    } catch (error) {
      console.error("Failed to fetch company branding", error);
    }
  };

  const handleEditCreative = (creative) => {
    setEditingCreative(creative);
    setEditorOpen(true);
  };

  const handleCreativeUpdate = (updatedData) => {
    // Update the creative in the list
    setCreatives(creatives.map(c => 
      c.id === updatedData.id ? { ...c, ...updatedData } : c
    ));
    fetchCreatives(); // Refresh to get full updated data
  };

  // Validate image dimensions
  const validateImageDimensions = (width, height, format) => {
    const required = FORMATS[format];
    if (width !== required.width || height !== required.height) {
      return `Image must be exactly ${required.width}×${required.height} pixels. Your image is ${width}×${height}.`;
    }
    return null;
  };

  // Get initial crop based on aspect ratio
  const getInitialCrop = (imageWidth, imageHeight, aspectRatio) => {
    const imageAspect = imageWidth / imageHeight;
    let cropWidth, cropHeight;
    
    if (imageAspect > aspectRatio) {
      // Image is wider than needed - crop width
      cropHeight = imageHeight;
      cropWidth = imageHeight * aspectRatio;
    } else {
      // Image is taller than needed - crop height
      cropWidth = imageWidth;
      cropHeight = imageWidth / aspectRatio;
    }
    
    // Center the crop
    const x = (imageWidth - cropWidth) / 2;
    const y = (imageHeight - cropHeight) / 2;
    
    return {
      unit: 'px',
      x,
      y,
      width: cropWidth,
      height: cropHeight
    };
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Create image to check dimensions and show crop interface
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      setUploadDimensions({ width: img.width, height: img.height });
      setOriginalImage(objectUrl);
      setUploadName(file.name.replace(/\.[^/.]+$/, ""));
      
      // Set up initial crop with correct aspect ratio
      const format = FORMATS[createFormat];
      const initialCrop = getInitialCrop(img.width, img.height, format.aspectRatio);
      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
      
      // Show crop interface
      setShowCrop(true);
      setDimensionError(null);
    };
    
    img.onerror = () => {
      toast.error("Could not load image");
      URL.revokeObjectURL(objectUrl);
    };
    
    img.src = objectUrl;
  };

  // Generate cropped image blob
  const getCroppedImageBlob = useCallback(async () => {
    if (!completedCrop || !cropImageRef.current) {
      return null;
    }
    
    const image = cropImageRef.current;
    const canvas = document.createElement('canvas');
    const format = FORMATS[createFormat];
    
    // Set canvas to target dimensions
    canvas.width = format.width;
    canvas.height = format.height;
    
    const ctx = canvas.getContext('2d');
    
    // Calculate scale factors (image might be displayed smaller than actual size)
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Draw the cropped portion scaled to fit the target dimensions
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      format.width,
      format.height
    );
    
    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png', 0.95);
    });
  }, [completedCrop, createFormat]);

  const handleCropComplete = async () => {
    if (!completedCrop) {
      toast.error("Please select a crop area");
      return;
    }
    
    try {
      const croppedBlob = await getCroppedImageBlob();
      if (!croppedBlob) {
        toast.error("Could not generate cropped image");
        return;
      }
      
      // Create file from blob
      const croppedFile = new File([croppedBlob], `${uploadName}.png`, { type: 'image/png' });
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      
      // Clean up original image URL
      if (originalImage) {
        URL.revokeObjectURL(originalImage);
      }
      
      setUploadFile(croppedFile);
      setUploadPreview(previewUrl);
      setShowCrop(false);
      setOriginalImage(null);
      
      const format = FORMATS[createFormat];
      setUploadDimensions({ width: format.width, height: format.height });
      
      toast.success("Image cropped successfully!");
    } catch (error) {
      console.error("Crop error:", error);
      toast.error("Failed to crop image");
    }
  };

  const handleCancelCrop = () => {
    if (originalImage) {
      URL.revokeObjectURL(originalImage);
    }
    setShowCrop(false);
    setOriginalImage(null);
    setCrop(null);
    setCompletedCrop(null);
    setUploadDimensions(null);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) {
      toast.error("Please select a file and enter a name");
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName);
      formData.append('format', createFormat);
      
      await axios.post(`${API}/creatives/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Creative uploaded successfully");
      resetForm();
      fetchCreatives();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt for AI generation");
      return;
    }
    
    setGenerating(true);
    try {
      const format = FORMATS[createFormat];
      const response = await axios.post(`${API}/creatives/generate`, {
        prompt: aiPrompt,
        name: aiName || `AI ${format.label} - ${aiPrompt.substring(0, 30)}...`,
        format: createFormat,
        width: format.width,
        height: format.height
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Creative generated successfully!");
      resetForm();
      fetchCreatives();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleURLImport = async () => {
    if (!urlInput.trim()) {
      toast.error("Please enter an image URL");
      return;
    }
    
    if (!urlCompletedCrop) {
      toast.error("Please adjust the crop area");
      return;
    }
    
    setSaving(true);
    try {
      // Get cropped image blob
      const croppedBlob = await getUrlCroppedImageBlob();
      if (!croppedBlob) {
        toast.error("Could not generate cropped image. The image may have CORS restrictions.");
        return;
      }
      
      // Upload cropped image as file
      const format = FORMATS[createFormat];
      const formData = new FormData();
      const fileName = urlName || "Imported from URL";
      formData.append('file', new File([croppedBlob], `${fileName}.png`, { type: 'image/png' }));
      formData.append('name', fileName);
      formData.append('format', createFormat);
      formData.append('width', format.width);
      formData.append('height', format.height);
      
      await axios.post(`${API}/creatives/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Creative imported successfully");
      resetForm();
      fetchCreatives();
    } catch (error) {
      console.error('URL import error:', error);
      if (error.message?.includes('CORS') || error.message?.includes('security')) {
        toast.error("Cannot process this image due to security restrictions. Please download the image first and upload it directly.");
      } else if (error.message?.includes('timed out')) {
        toast.error("Export timed out. Please try a different image or download and upload directly.");
      } else {
        toast.error(error.response?.data?.detail || error.message || "Import failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleURLCheck = (url) => {
    setUrlInput(url);
    setUrlDimensions(null);
    setUrlDimensionError(null);
    setShowUrlCrop(false);
    setUrlOriginalImage(null);
    
    if (!url.trim()) return;
    
    // Load image to get dimensions and prepare for cropping
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      setUrlDimensions({ width: img.width, height: img.height });
      setUrlOriginalImage(url);
      
      // Set up initial crop with correct aspect ratio
      const format = FORMATS[createFormat];
      const initialCrop = getInitialCrop(img.width, img.height, format.aspectRatio);
      setUrlCrop(initialCrop);
      setUrlCompletedCrop(initialCrop);
      
      // Show crop interface
      setShowUrlCrop(true);
      setUrlDimensionError(null);
    };
    
    img.onerror = () => {
      setUrlDimensionError("Could not load image from URL. Make sure it's a valid image URL with CORS enabled.");
    };
    
    img.src = url;
  };

  // Generate cropped image blob from URL
  const getUrlCroppedImageBlob = useCallback(async () => {
    if (!urlCompletedCrop || !urlCropImageRef.current) {
      return null;
    }
    
    const image = urlCropImageRef.current;
    const canvas = document.createElement('canvas');
    const format = FORMATS[createFormat];
    
    // Set canvas to target dimensions
    canvas.width = format.width;
    canvas.height = format.height;
    
    const ctx = canvas.getContext('2d');
    
    // Calculate scale factors (image might be displayed smaller than actual size)
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    try {
      // Draw the cropped portion scaled to fit the target dimensions
      ctx.drawImage(
        image,
        urlCompletedCrop.x * scaleX,
        urlCompletedCrop.y * scaleY,
        urlCompletedCrop.width * scaleX,
        urlCompletedCrop.height * scaleY,
        0,
        0,
        format.width,
        format.height
      );
      
      // Check if canvas is tainted by trying to get image data
      // This will throw a SecurityError if CORS-tainted
      try {
        ctx.getImageData(0, 0, 1, 1);
      } catch (securityError) {
        throw new Error('CORS error: Cannot export image from this URL. Try downloading the image first and uploading it.');
      }
      
      return new Promise((resolve, reject) => {
        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          reject(new Error('Export timed out. The image may have security restrictions.'));
        }, 10000);
        
        canvas.toBlob((blob) => {
          clearTimeout(timeout);
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas. The image may have security restrictions.'));
          }
        }, 'image/png', 0.95);
      });
    } catch (e) {
      console.error('Canvas operation error:', e);
      throw e; // Re-throw so the caller can handle it
    }
  }, [urlCompletedCrop, createFormat]);

  const handleDelete = async () => {
    if (!creativeToDelete) return;
    
    try {
      await axios.delete(`${API}/creatives/${creativeToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Creative deleted");
      setDeleteDialogOpen(false);
      setCreativeToDelete(null);
      fetchCreatives();
    } catch (error) {
      toast.error("Failed to delete creative");
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setCreateMode(null);
    setCreateFormat(null);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadName("");
    setUploadDimensions(null);
    setDimensionError(null);
    // Reset crop state
    setShowCrop(false);
    setCrop(null);
    setCompletedCrop(null);
    if (originalImage) {
      URL.revokeObjectURL(originalImage);
    }
    setOriginalImage(null);
    // Reset URL crop state
    setShowUrlCrop(false);
    setUrlCrop(null);
    setUrlCompletedCrop(null);
    setUrlOriginalImage(null);
    // Reset other form state
    setAiPrompt("");
    setAiName("");
    setUrlInput("");
    setUrlName("");
    setUrlDimensions(null);
    setUrlDimensionError(null);
  };

  const openCreateDialog = (format, mode) => {
    setCreateFormat(format);
    setCreateMode(mode);
    setDialogOpen(true);
  };

  const bannerCreatives = creatives.filter(c => c.format === 'BANNER');
  const panelCreatives = creatives.filter(c => c.format === 'PANEL');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="creatives-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Reusable creative card component
  const CreativeCard = ({ creative, format }) => {
    const [showFullSize, setShowFullSize] = useState(false);
    
    return (
      <>
        <Card 
          key={creative.id} 
          className="bg-card border-border overflow-hidden group hover:shadow-lg transition-all"
          data-testid={`creative-${creative.id}`}
        >
          <div 
            className="relative bg-secondary/50 cursor-pointer"
            style={{ aspectRatio: format === 'BANNER' ? '320/100' : '16/9' }}
            onClick={() => setShowFullSize(true)}
          >
            <img 
              src={creative.file_url} 
              alt={creative.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.background = '#27272A'; e.target.alt = 'No Preview'; }}
            />
            {/* Hover overlay with action buttons */}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Action buttons - positioned at bottom for better visibility */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/40 rounded-lg p-1">
                <Button 
                  variant="secondary" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); handleEditCreative(creative); }}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button 
                  variant="secondary" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); setShowFullSize(true); }}
                  title="View Full Size"
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => { e.stopPropagation(); setCreativeToDelete(creative); setDeleteDialogOpen(true); }}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* Source badge */}
            <Badge 
              className={`absolute top-2 right-2 text-xs ${
                creative.source === 'AI_GENERATED' 
                  ? 'bg-purple-500/80' 
                  : creative.source === 'URL_IMPORTED'
                    ? 'bg-green-500/80'
                    : 'bg-blue-500/80'
              }`}
            >
              {creative.source === 'AI_GENERATED' ? 'AI' : creative.source === 'URL_IMPORTED' ? 'URL' : 'Uploaded'}
            </Badge>
          </div>
          <CardContent className="p-3">
            <h4 className="font-medium truncate text-sm">{creative.name}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(creative.created_at).toLocaleDateString()}
            </p>
          </CardContent>
        </Card>
        
        {/* Full Size View Dialog */}
        <Dialog open={showFullSize} onOpenChange={setShowFullSize}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-4">
            <DialogHeader>
              <DialogTitle>{creative.name}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center">
              <img 
                src={creative.file_url} 
                alt={creative.name}
                className="max-w-full max-h-[80vh] object-contain"
              />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              {creative.width} × {creative.height} pixels
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  // Reusable column component
  const FormatColumn = ({ format, creativesList }) => {
    const formatInfo = FORMATS[format];
    const FormatIcon = formatInfo.icon;
    
    return (
      <div className="flex-1 min-w-0">
        {/* Column Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <FormatIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold">{formatInfo.label}s</h2>
            <p className="text-xs text-muted-foreground">
              Required: <span className="font-mono text-primary">{formatInfo.ratio}</span>
            </p>
          </div>
          <Badge variant="outline" className="ml-auto text-xs">
            {creativesList.length}
          </Badge>
        </div>

        {/* Format Description */}
        <p className="text-sm text-muted-foreground mb-4">
          {formatInfo.description}
        </p>

        {/* Create Options */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            className="flex-col h-auto py-3 hover:border-blue-500/50 hover:bg-blue-500/5"
            onClick={() => openCreateDialog(format, 'upload')}
          >
            <Upload className="h-5 w-5 text-blue-500 mb-1" />
            <span className="text-xs">Upload</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-col h-auto py-3 hover:border-purple-500/50 hover:bg-purple-500/5"
            onClick={() => openCreateDialog(format, 'ai')}
          >
            <Sparkles className="h-5 w-5 text-purple-500 mb-1" />
            <span className="text-xs">AI Generate</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-col h-auto py-3 hover:border-green-500/50 hover:bg-green-500/5"
            onClick={() => openCreateDialog(format, 'url')}
          >
            <Link className="h-5 w-5 text-green-500 mb-1" />
            <span className="text-xs">From URL</span>
          </Button>
        </div>

        {/* Creatives Grid - Responsive */}
        {creativesList.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <Image className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No {formatInfo.label.toLowerCase()}s yet
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className={`grid gap-3 ${
            creativesList.length === 1 ? 'grid-cols-1' :
            creativesList.length === 2 ? 'grid-cols-2' :
            'grid-cols-3'
          }`}>
            {creativesList.map(creative => (
              <CreativeCard key={creative.id} creative={creative} format={format} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const formatInfo = createFormat ? FORMATS[createFormat] : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="creatives-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tight">Creative Builder</h1>
        <p className="text-muted-foreground mt-1">
          Create and manage ad creatives for your campaigns
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <FormatColumn format="BANNER" creativesList={bannerCreatives} />
        <FormatColumn format="PANEL" creativesList={panelCreatives} />
      </div>

      {/* Creative Editor */}
      <CreativeEditor
        creative={editingCreative}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        token={token}
        companyAssets={companyAssets}
        brandColors={companyBranding?.colors || []}
        brandFonts={companyBranding?.fonts || []}
        onUpdate={handleCreativeUpdate}
      />

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {createMode === 'upload' && <><Upload className="h-5 w-5 text-blue-500" /> Upload {formatInfo?.label}</>}
              {createMode === 'ai' && <><Sparkles className="h-5 w-5 text-purple-500" /> Generate {formatInfo?.label} with AI</>}
              {createMode === 'url' && <><Link className="h-5 w-5 text-green-500" /> Import {formatInfo?.label} from URL</>}
            </DialogTitle>
            <DialogDescription>
              Required dimensions: <span className="font-mono font-semibold text-primary">{formatInfo?.ratio}</span>
            </DialogDescription>
          </DialogHeader>

          {/* Upload Form */}
          {createMode === 'upload' && (
            <div className="space-y-4">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
              
              {/* Crop Interface */}
              {showCrop && originalImage ? (
                <div className="space-y-4">
                  <div className="bg-secondary/30 rounded-lg p-2">
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                      <Crop className="h-3 w-3" />
                      Drag to adjust crop area. Aspect ratio is locked to <span className="font-mono font-semibold">{formatInfo?.ratio}</span>
                    </p>
                    <div className="flex justify-center overflow-hidden" style={{ maxHeight: '400px', position: 'relative' }}>
                      <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={FORMATS[createFormat]?.aspectRatio}
                        minWidth={50}
                        minHeight={50}
                        style={{ maxHeight: '380px', maxWidth: '100%' }}
                      >
                        <img 
                          ref={cropImageRef}
                          src={originalImage} 
                          alt="Crop preview"
                          style={{ maxHeight: '380px', maxWidth: '100%', display: 'block' }}
                          onLoad={(e) => {
                            // Recalculate crop when image loads to ensure proper positioning
                            const img = e.currentTarget;
                            const format = FORMATS[createFormat];
                            const initialCrop = getInitialCrop(img.width, img.height, format.aspectRatio);
                            setCrop(initialCrop);
                            setCompletedCrop(initialCrop);
                          }}
                        />
                      </ReactCrop>
                    </div>
                    {uploadDimensions && (
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Original: {uploadDimensions.width}×{uploadDimensions.height}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={handleCancelCrop}>
                      <X className="mr-2 h-4 w-4" /> Cancel
                    </Button>
                    <Button className="flex-1" onClick={handleCropComplete}>
                      <Crop className="mr-2 h-4 w-4" /> Apply Crop
                    </Button>
                  </div>
                </div>
              ) : uploadPreview ? (
                <div className="relative rounded-lg overflow-hidden border border-border" style={{ aspectRatio: createFormat === 'BANNER' ? '320/100' : '16/9' }}>
                  <img src={uploadPreview} alt="Preview" className="w-full h-full object-cover" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => { 
                      setUploadFile(null); 
                      setUploadPreview(null); 
                      setUploadDimensions(null);
                      setDimensionError(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {uploadDimensions && (
                    <Badge className="absolute bottom-2 left-2 bg-black/70">
                      {uploadDimensions.width}×{uploadDimensions.height} ✓
                    </Badge>
                  )}
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ImagePlus className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Click to select an image
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Any size - you'll crop to <span className="font-mono font-semibold">{formatInfo?.ratio}</span>
                  </p>
                </div>
              )}

              {/* Dimension Error */}
              {dimensionError && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Invalid Dimensions</p>
                    <p className="text-xs text-destructive/80">{dimensionError}</p>
                  </div>
                </div>
              )}

              {/* Name input and upload button - only show when not in crop mode */}
              {!showCrop && (
                <>
                  <div className="space-y-2">
                    <Label>Creative Name</Label>
                    <Input
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder={`My ${formatInfo?.label} Ad`}
                    />
                  </div>

                  <Button 
                    className="w-full" 
                    onClick={handleUpload} 
                    disabled={!uploadFile || !uploadName.trim() || saving}
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    Upload Creative
                  </Button>
                </>
              )}
            </div>
          )}

          {/* AI Generation Form */}
          {createMode === 'ai' && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="h-5 w-5 text-purple-500" />
                  <span className="font-medium text-purple-600">AI Image Generation</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  AI will generate an image at exactly <span className="font-mono font-semibold">{formatInfo?.ratio}</span> resolution.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Prompt *</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="A modern coffee shop scene with warm lighting, featuring a steaming latte on a wooden table..."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Describe the scene, colors, mood, and style you want
                </p>
              </div>

              <div className="space-y-2">
                <Label>Creative Name (optional)</Label>
                <Input
                  value={aiName}
                  onChange={(e) => setAiName(e.target.value)}
                  placeholder="Auto-generated from prompt"
                />
              </div>

              <Button 
                className="w-full bg-purple-600 hover:bg-purple-700" 
                onClick={handleAIGenerate} 
                disabled={!aiPrompt.trim() || generating}
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating... (this may take 15-30s)
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate {formatInfo?.label}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* URL Import Form */}
          {createMode === 'url' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Image URL *</Label>
                <Input
                  value={urlInput}
                  onChange={(e) => handleURLCheck(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  disabled={showUrlCrop}
                />
                <p className="text-xs text-muted-foreground">
                  Paste any image URL - you'll crop it to <span className="font-mono font-semibold">{formatInfo?.ratio}</span>
                </p>
              </div>

              {/* URL Crop Interface */}
              {showUrlCrop && urlOriginalImage && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crop className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">Adjust crop area</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowUrlCrop(false);
                        setUrlOriginalImage(null);
                        setUrlInput("");
                        setUrlDimensions(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Change URL
                    </Button>
                  </div>
                  
                  <div className="border border-border rounded-lg overflow-hidden bg-secondary/30 p-2">
                    <ReactCrop
                      crop={urlCrop}
                      onChange={(c) => setUrlCrop(c)}
                      onComplete={(c) => setUrlCompletedCrop(c)}
                      aspect={formatInfo?.aspectRatio}
                      className="max-h-[400px] mx-auto"
                    >
                      <img
                        ref={urlCropImageRef}
                        src={urlOriginalImage}
                        alt="Crop preview"
                        crossOrigin="anonymous"
                        style={{ maxHeight: '400px', width: 'auto' }}
                        onLoad={(e) => {
                          const img = e.currentTarget;
                          const format = FORMATS[createFormat];
                          const initialCrop = getInitialCrop(img.width, img.height, format.aspectRatio);
                          setUrlCrop(initialCrop);
                          setUrlCompletedCrop(initialCrop);
                        }}
                      />
                    </ReactCrop>
                  </div>
                  
                  {urlDimensions && (
                    <p className="text-xs text-muted-foreground text-center">
                      Original: {urlDimensions.width}×{urlDimensions.height} → Output: {formatInfo?.ratio}
                    </p>
                  )}
                </div>
              )}

              {/* URL Load Error */}
              {urlDimensionError && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">Could not load image</p>
                    <p className="text-xs text-destructive/80">{urlDimensionError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Creative Name (optional)</Label>
                <Input
                  value={urlName}
                  onChange={(e) => setUrlName(e.target.value)}
                  placeholder="Imported from URL"
                />
              </div>

              <Button 
                className="w-full bg-green-600 hover:bg-green-700" 
                onClick={handleURLImport} 
                disabled={!showUrlCrop || saving}
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Import {formatInfo?.label}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Creative?</DialogTitle>
            <DialogDescription>
              This will permanently delete &ldquo;{creativeToDelete?.name}&rdquo;. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
